import { Fragment, useEffect, useState, useRef } from "react";
import LoadingPlaceholder from "../../../../components/ui/loading/LoadingPlaceholder";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getUserRole } from "../../../../utils/auth";
import PTFTaxSituationSection from "./utils/PTFTaxSituationSection";
import BTFTaxSituationSection from "./utils/BTFTaxSituationSection";
import CompletedFilingPackage from "../../../../components/ui/filings/CompletedFilingPackage";
import CreditsDeductionsSection from "./sections/CreditsDeductionsSection";
import DependentsSection from "./sections/DependentsSection";
import InsuranceSection from "./sections/InsuranceSection";
import BackgroundSection from "./sections/BackgroundSection";
import AdditionalNotesSection from "./sections/AdditionalNotesSection";
import { checkAllTaxSituationComplete } from "./utils/hooks/filingValidationHelpers";
import ExpertDocumentsSection from "./sections/ExpertDocumentsSection";
import { useFilingModals } from "./utils/hooks/useFilingModals";
import { useDependentActions } from "./utils/hooks/useDependentActions";
import { useFilingData } from "./utils/hooks/useFilingData";
import { useDocumentActions } from "./utils/hooks/useDocumentActions";
import { useFilingActions } from "./utils/hooks/useFilingActions";
import { useFilingUI } from "./utils/hooks/useFilingUI";
import { useFilingNotifications } from "./utils/hooks/useFilingNotifications";
import { useFilingRedirects } from "./utils/hooks/useFilingRedirects";
import FilingModalOrchestrator from "./modals/orchestrator/FilingModalOrchestrator";

function FilingOverviewV2() {
  const { filingUuid } = useParams();
  const [pendingFeedbackType, setPendingFeedbackType] = useState(null); // 'demo' or 'regular'
  const [hasCheckedReview, setHasCheckedReview] = useState(false);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = getUserRole();
  const isCpa = userRole === "CPA";

  // Filing data needed
  const filingData = useFilingData(filingUuid);
  const {
    loading,
    filing,
    setFiling,
    documents,
    dependents,
    setDependents,
    fetchData,
    fetchDocuments,
    handleDocumentUpdated,
  } = filingData;

  const dependentActions = useDependentActions(
    filingUuid,
    dependents,
    setDependents,
    setFiling,
  );
  const { setEditingDependentIndex, setDeleteDependentIndex } =
    dependentActions;

  const {
    handleUploadDocument,
    handleDocumentDeleted,
    handleMarkCryptoComplete,
  } = useDocumentActions(
    filingUuid,
    filing,
    setFiling,
    fetchDocuments,
    fetchData,
  );

  // Filing actions
  const filingActions = useFilingActions(
    filingUuid,
    filing,
    fetchData,
    fetchDocuments,
  );

  // Define this near your other constants/state
  const ptfTaxYears = filing.tax_situation?.details?.PTF?.situation?.taxYears;
  const taxYearsArray = Array.isArray(ptfTaxYears)
    ? ptfTaxYears
    : ptfTaxYears
    ? [ptfTaxYears]
    : [];

  // Top-level flag: All e-files accepted and signature auth complete
  // Used to disable document deletion for CUS when only Proof of Acceptance is left
  const allEfilesComplete =
    filing?.summary_metrics?.expert_documents?.all_efile_complete || false;

  // Modal state
  const modalState = useFilingModals();
  const {
    setIsDependentModalOpen,
    setShowDemoReviewModal,
    setShowCusReviewIntroModal,
    setIsSigningModalOpen,
  } = modalState;

  // New UI Coordinator Hook
  const filingUI = useFilingUI({
    modalState,
    filingActions,
    filingData,
    isCpa,
    setPendingFeedbackType,
    filingUuid,
    filing,
    navigate,
    setHasCheckedReview,
    setShowDemoReviewModal,
    setShowCusReviewIntroModal,
    setIsSigningModalOpen,
    setEditingDependentIndex,
    setDeleteDependentIndex,
    setIsDependentModalOpen,
  });
  const {
    handleOpenUploadModal,
    handleOpenGenerateGainLossModal,
    handleAddDependent,
    handleEditDependent,
    handleDeleteDependent,
    handleEnableCryptoUpload,
    checkAndShowReviewModal,
  } = filingUI;

  // Check if filing is complete and if user has submitted a review
  useEffect(() => {
    if (!loading && filing && filing.status === "COM" && !hasCheckedReview) {
      checkAndShowReviewModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, filing]);

  // Notification hook
  useFilingNotifications(filingUuid, isCpa, fetchData);

  // URL Param Logic
  useFilingRedirects({
    isCpa,
    searchParams,
    setSearchParams,
    modalState,
  });

  // Render accordions based on services
  const renderAccordions = () => {
    const services = Array.isArray(filing.tax_service?.services)
      ? filing.tax_service.services
      : [];
    const accordions = [];

    // Taxpayer background info
    accordions.push(<BackgroundSection filing={filing} services={services} />);

    // Personal Tax Filing (PTF) - Tax Situation and Dependents
    if (services.includes("PTF")) {
      const ptfSituation = filing.tax_situation?.details?.PTF?.situation;

      // Tax Situation section
      if (ptfSituation) {
        accordions.push(
          <PTFTaxSituationSection
            key="tax-situation"
            situation={ptfSituation}
            documents={documents}
            isCpa={isCpa}
            onOpenUploadModal={handleOpenUploadModal}
            onDocumentDeleted={handleDocumentDeleted}
            onMarkCryptoComplete={handleMarkCryptoComplete}
            onEnableCryptoUpload={handleEnableCryptoUpload}
            onOpenGenerateGainLossModal={handleOpenGenerateGainLossModal}
            summaryMetrics={filing.summary_metrics}
            disableDelete={!isCpa && allEfilesComplete}
          />,
        );
      }

      // Credits & Deductions accordion
      accordions.push(
        <CreditsDeductionsSection
          filing={filing}
          documents={documents}
          isCpa={isCpa}
          handleOpenUploadModal={handleOpenUploadModal}
          handleDocumentDeleted={handleDocumentDeleted}
          allEfilesComplete={allEfilesComplete}
        />,
      );

      // Insurance accordion
      accordions.push(
        <InsuranceSection
          filing={filing}
          documents={documents}
          isCpa={isCpa}
          handleOpenUploadModal={handleOpenUploadModal}
          handleDocumentDeleted={handleDocumentDeleted}
          allEfilesComplete={allEfilesComplete}
        />,
      );

      // Dependents accordion
      accordions.push(
        <DependentsSection
          filing={filing}
          dependents={dependents}
          isCpa={isCpa}
          onAddDependent={handleAddDependent}
          onEditDependent={handleEditDependent}
          onDeleteDependent={handleDeleteDependent}
        />,
      );
    }

    // Business Tax Filing (BTF) - Tax Situation only (Background is now in combined accordion)
    if (services.includes("BTF")) {
      const btfSituation = filing.tax_situation?.details?.BTF?.situation;

      // Business Tax Situation
      if (btfSituation) {
        accordions.push(
          <BTFTaxSituationSection
            key="business-tax-situation"
            situation={btfSituation}
            documents={documents}
            isCpa={isCpa}
            onOpenUploadModal={handleOpenUploadModal}
            onDocumentDeleted={handleDocumentDeleted}
            summaryMetrics={filing.summary_metrics}
            disableDelete={!isCpa && allEfilesComplete}
          />,
        );
      }
    }

    // Additional Notes accordion
    accordions.push(<AdditionalNotesSection filing={filing} />);

    const allTaxSituationComplete = checkAllTaxSituationComplete(
      filing,
      documents,
      services,
      dependents,
    );

    // Expert Documents section (general for both PTF and BTF)
    accordions.push(
      <ExpertDocumentsSection
        filing={filing}
        setFiling={setFiling}
        filingUuid={filingUuid}
        documents={documents}
        isCpa={isCpa}
        allTaxSituationComplete={allTaxSituationComplete}
        handleOpenUploadModal={handleOpenUploadModal}
        handleDocumentDeleted={handleDocumentDeleted}
        handleDocumentUpdated={handleDocumentUpdated}
      />,
    );

    return accordions;
  };

  return (
    <Fragment>
      {loading && <LoadingPlaceholder rows={3} type="loading-line" />}
      {!loading && (
        <Fragment>
          {/* Show download package banner for completed filings (CUS only) */}
          {filing.status === "COM" && !isCpa && (
            <CompletedFilingPackage filingId={filingUuid} />
          )}
          {renderAccordions()}
        </Fragment>
      )}

      {/* The Orchestrator handles the Modals */}
      <FilingModalOrchestrator
        ui={filingUI}
        state={modalState}
        data={filingData}
        actionsDocument={{ handleUploadDocument }}
        actionsDependent={dependentActions}
        isCpa={isCpa}
        filingUuid={filingUuid}
        taxYearsArray={taxYearsArray}
        setFiling={setFiling}
      />
    </Fragment>
  );
}

export default FilingOverviewV2;
